import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, input, OnInit } from '@angular/core';
import { ItemsComponent } from '@common/classes/items.component.abstract';
import { TicketPermissions } from '@common/classes/permissions';
import { ColumnSettings } from '@common/interfaces/column-settings.interface';
import { BackendHttpErrorResponse } from '@common/interfaces/error-response.interface';
import { CurrencyCodes } from '@common/known-types/currency.codes';
import { Sources } from '@common/known-types/sources';
import { TariffTypeCodes } from '@common/known-types/tariff-type.codes';
import { ViewMode } from '@common/models/view-mode';
import { CommonService } from '@common/services/common.service';
import { ValidationConstants } from '@common/validation-constants';
import { DialogSettings } from '@progress/kendo-angular-dialog';
import { ExchangeRateModel } from 'app/invoice/models/invoice-view.model';
import { TicketBaseModel } from 'app/ticket/models/ticket-base.model';
import { TicketListModel } from 'app/ticket/models/ticket-list.model';
import { ticketBaseColumns, ticketQuickFilters } from 'app/ticket/ticket-helper';
import _ from 'lodash';
import { FormProperty } from '../app-control/app-control.component';

@Component({
    selector: 'app-tickets',
    templateUrl: './tickets.component.html',
    providers: [DecimalPipe, DatePipe]
})
export class TicketsComponent extends ItemsComponent<TicketListModel> implements OnInit {
    protected override command = 'SaveTicket';
    protected override itemName = 'Ticket';
    protected override itemCreatePermission: string;
    protected override itemEditPermission = TicketPermissions.Action.Edit;
    protected override itemRemovePermission: string;
    protected override dialogProperties: FormProperty[] = [
        // {
        //     name: 'quantity',
        //     label: 'Quantity',
        //     type: 'number',
        //     format: 'n2',
        //     required: true,
        //     colSpan: 6
        // },
        {
            name: 'quantity2',
            label: 'Quantity 2',
            type: 'number',
            format: ValidationConstants.FinancialLimits.QuantityFormat,
            decimals: ValidationConstants.FinancialLimits.QuantityDecimals,
            min: ValidationConstants.FinancialLimits.MinQuantity,
            max: ValidationConstants.FinancialLimits.MaxQuantity,
            colSpan: 12
        },
        {
            name: 'customerId',
            label: 'Customer',
            type: 'codelist',
            codelist: 'Customer',
            colSpan: 12
        },
        {
            name: 'description',
            label: 'Description',
            type: 'textarea',
            rows: 3,
            colSpan: 12
        }
    ];
    override columns: ColumnSettings<keyof TicketBaseModel>[] = ticketBaseColumns;
    protected override dialogSettings: DialogSettings = { width: '500px' };

    naturalParentEntityName = input<string>();
    generateTicketsCommand = input<string>();
    showUserGenerateTickets = input<boolean>();
    billTicketsCommand = input<string>();
    showTariffTypeFilters = input<boolean>(true);
    defaultDocumentTypeId = input<string>();
    defaultLocationId = input<string>();

    filteredData: Partial<TicketBaseModel>[];
    quickFilters = ticketQuickFilters;

    constructor(
        protected override commonService: CommonService,
        private decimalPipe: DecimalPipe,
        private datePipe: DatePipe
    ) {
        super(commonService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.filter = {};
        this.getFilteredData();

        this.gridButtons.push({
            label: 'Actions',
            items: [
                {
                    tooltip: 'Bill',
                    icon: 'faSolidCashRegister',
                    iconOnly: true,
                    variant: 'primary',
                    isVisible: () =>
                        this.user.hasPermission(TicketPermissions.Action.Bill) && this.mode === ViewMode.view,
                    isDisabled: () =>
                        this.isActionsDisabled() ||
                        this.selection.length === 0 ||
                        this.anySelectedTicketsInvoiced() ||
                        this.anySelectedTicketsInactive(),
                    onClick: async () => await this.billTickets(),
                    badgeCount: () => this.selection.length
                },
                {
                    tooltip: 'Toggle',
                    icon: 'faSolidTicket',
                    iconOnly: true,
                    variant: 'primary',
                    isVisible: () =>
                        this.user.hasPermission(TicketPermissions.Action.Edit) && this.mode === ViewMode.view,
                    isDisabled: () =>
                        this.isActionsDisabled() || this.selection.length === 0 || this.anySelectedTicketsInvoiced(),
                    onClick: async () => await this.toggleTickets(),
                    badgeCount: () => this.selection.length
                },
                {
                    tooltip: 'Insert Quantity',
                    icon: 'faSolidCalculator',
                    iconOnly: true,
                    variant: 'primary',
                    isVisible: () =>
                        this.user.hasPermission(TicketPermissions.Action.Edit) && this.mode === ViewMode.view,
                    isDisabled: () =>
                        this.isActionsDisabled() || this.selection.length < 2 || this.anySelectedTicketsInvoiced(),
                    onClick: async () => await this.insertQuantity(),
                    badgeCount: () => this.selection.length
                },
                {
                    tooltip: 'Bill Now',
                    icon: 'faSolidCashRegister',
                    iconOnly: true,
                    variant: 'excel',
                    isVisible: () =>
                        this.showUserGenerateTickets() &&
                        this.user.hasPermission(TicketPermissions.VesselVisit.Generate) &&
                        this.mode === ViewMode.view,
                    onClick: async () => await this.userGenerateTickets()
                },
                {
                    tooltip: 'Generate',
                    icon: 'faSolidPlus',
                    iconOnly: true,
                    variant: 'warning',
                    isVisible: () =>
                        this.user.hasPermission(TicketPermissions.Action.Debug) && this.mode === ViewMode.view,
                    onClick: async () => await this.debugGenerateTickets()
                },
                {
                    tooltip: 'Remove',
                    icon: 'faSolidTrash',
                    iconOnly: true,
                    variant: 'warning',
                    isVisible: () =>
                        this.user.hasPermission(TicketPermissions.Action.Debug) && this.mode === ViewMode.view,
                    isDisabled: () => this.selection.length === 0 || this.anySelectedTicketsInvoiced(),
                    onClick: async () => await this.debugDeleteTickets(),
                    badgeCount: () => this.selection.length
                }
            ]
        });
    }

    override set data(val: Partial<TicketBaseModel>[]) {
        super.data = val;
        this.getFilteredData();
    }

    protected override isActionsDisabled(): boolean {
        return this.anySelectedTicketsInvoiced() || super.isActionsDisabled();
    }

    override async openDialog(item: any = null): Promise<void> {
        return super.openDialog(item).then(() => this.getFilteredData());
    }

    private anySelectedTicketsInvoiced(): boolean {
        return this.selection.filter((selection) => selection.invoiceId).length > 0;
    }

    private anySelectedTicketsInactive(): boolean {
        return this.selection.filter((selection) => !selection.active).length > 0;
    }

    private async billTickets(): Promise<void> {
        const properties: FormProperty[] = [];

        properties.push(
            {
                name: 'documentTypeId',
                label: 'Document Type',
                type: 'codelist',
                codelist: 'DocumentType',
                customFilter: 'DocumentType.CreditNote=false',
                required: this.billTicketsCommand() !== 'BillContractTickets',
                initialValue:
                    this.selection.at(0).vesselVisitSourceId === Sources.RijekaTraffic &&
                    this.selection.filter((item) =>
                        [TariffTypeCodes.ElectricityCosts, TariffTypeCodes.WaterCosts].includes(
                            item.tariffTypeId as TariffTypeCodes
                        )
                    ).length > 0
                        ? undefined
                        : (this.defaultDocumentTypeId() ?? undefined),
                colSpan: 8
            },
            {
                name: 'groupByService',
                label: 'Group by Service',
                type: 'boolean',
                initialValue: true,
                colSpan: 4
            }
        );

        if (this.billTicketsCommand() == 'BillVesselVisitTickets') {
            if (this.selection.at(0).vesselVisitSourceId === Sources.PCS)
                properties.push({
                    name: 'locationId',
                    label: 'Organization Unit (shore)',
                    type: 'codelist',
                    codelist: 'Location',
                    required: true,
                    initialValue: this.defaultLocationId() ?? undefined,
                    colSpan: 6
                });

            if (this.selection.at(0).vesselVisitSourceId === Sources.RijekaTraffic)
                properties.push({
                    name: 'customerId',
                    label: 'Customer',
                    type: 'codelist',
                    codelist: 'Customer',
                    initialValue: _.sortBy(this.selection, (sel) => sel.id).at(0).lkcVesselOwnerCustomerId,
                    required: true,
                    colSpan: 6
                });
        }

        properties.push({
            name: 'invoiceDate',
            label: 'Invoice Date',
            type: 'date',
            initialValue: _.sortBy(this.selection, (sel) => sel.invoiceDate).at(-1).invoiceDate,
            colSpan: 12
        });

        if (this.billTicketsCommand() == 'BillContractTickets') {
            properties.push({
                name: 'paymentDueDate',
                label: 'Payment Due Date',
                type: 'date',
                colSpan: 12
            });
        }

        if (properties.length == 4) {
            properties.at(-1).colSpan = 6;
            properties.at(-2).colSpan = 6;
        }

        const dialogResult = await this.dialogService.form({
            options: {
                title: this.translateService.instant('Bill'),
                titleIcons: ['faSolidCashRegister'],
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to bill the selected Tickets'
                )}?`,
                properties,
                canConfirm: (model) =>
                    properties
                        .filter((prop) => prop.required)
                        .map((prop) => model[prop.name])
                        .filter((value) => !value).length === 0
            }
        });
        if (!dialogResult) return;

        const ticketsInForeignCurrency = this.selection.filter((item) => item.currencyId !== CurrencyCodes.Eur);
        if (ticketsInForeignCurrency.length > 0) {
            const foreignCurrencyId = ticketsInForeignCurrency.at(0).currencyId;
            const exchangeRate = (await Promise.resolve(
                this.commonService.queryService.query('ExchangeRate', {
                    currency: foreignCurrencyId,
                    // current date is taken per https://redmine.actual-it.si/issues/41851
                    date: new Date()
                })
            )) as ExchangeRateModel;

            const exchangeRateDialog = await this.dialogService.confirm({
                options: {
                    title: this.translateService.instant('Exchange Rate'),
                    titleIcons: ['faSolidCashRegister'],
                    message: `${this.commonService.translateService.instant('Items will be converted from')} ${exchangeRate?.currencyId} ${this.commonService.translateService.instant('to')} ${CurrencyCodes.Eur} ${this.commonService.translateService.instant('at the exchange rate of')} ${this.decimalPipe.transform(1, '1.2-2')} ${CurrencyCodes.Eur} = ${this.decimalPipe.transform(exchangeRate?.rate, '1.4-4')} ${exchangeRate?.currencyId} ${this.commonService.translateService.instant('on')} ${this.datePipe.transform(exchangeRate?.rateDate, 'shortDate')}`
                }
            });
            if (!exchangeRateDialog) return;
        }

        this.executeCommand({
            command: this.billTicketsCommand(),
            data: {
                ticketIds: this.selection.map((selection) => selection.id),
                documentTypeId: dialogResult.documentTypeId,
                locationId: dialogResult.locationId,
                customerId: dialogResult.customerId,
                invoiceDate: dialogResult.invoiceDate,
                paymentDueDate: dialogResult.paymentDueDate,
                groupByService: dialogResult.groupByService,
                confirmDifferentShippingLines: false
            }
        })
            .then((invoiceId) => {
                if (invoiceId) {
                    this.router.navigate([`/invoice/view/${invoiceId}`]);
                }
            })
            .catch(async (error: BackendHttpErrorResponse) => {
                // Check if this is a shipping line confirmation error
                if (
                    error.error.errorMessage?.includes(
                        'You have selected items from different shipping lines for billing'
                    )
                ) {
                    const confirmed = await this.showShippingLineConfirmationDialog();
                    if (confirmed) {
                        // Retry with confirmation
                        const invoiceId = await this.executeCommand({
                            command: this.billTicketsCommand(),
                            data: {
                                ticketIds: this.selection.map((selection) => selection.id),
                                documentTypeId: dialogResult.documentTypeId,
                                locationId: dialogResult.locationId,
                                customerId: dialogResult.customerId,
                                invoiceDate: dialogResult.invoiceDate,
                                groupByService: dialogResult.groupByService,
                                confirmDifferentShippingLines: true
                            }
                        });

                        if (invoiceId) {
                            this.router.navigate([`/invoice/view/${invoiceId}`]);
                        }
                    }
                } else {
                    // Re-throw other errors to be handled by the normal error handling
                    throw error;
                }
            });
    }

    private async showShippingLineConfirmationDialog(): Promise<boolean> {
        return await this.dialogService.confirm({
            options: {
                title: this.translateService.instant('Warning'),
                titleIcons: ['faSolidTriangleExclamation'],
                message: `${this.translateService.instant('You have selected items from different shipping lines for billing')}.

${this.translateService.instant('Are you sure you want to bill items from different shipping lines')}?`
            }
        });
    }

    private async toggleTickets(): Promise<void> {
        const hasActiveTickets = this.selection.some((ticket) => ticket.active);

        const dialogResult = await this.dialogService.form({
            options: {
                title: this.translateService.instant('Toggle'),
                titleIcons: ['faSolidTicket'],
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to toggle the selected Tickets'
                )}? ${this.commonService.translateService.instant(
                    'Active Tickets will become Inactive and Inactive ones will become Active'
                )}.`,
                properties: hasActiveTickets
                    ? [
                          {
                              name: 'remark',
                              label: 'Remark',
                              type: 'textarea',
                              rows: 3,
                              maxlength: 255,
                              required: false,
                              colSpan: 12
                          }
                      ]
                    : []
            }
        });
        if (!dialogResult) return;

        await this.executeCommand({
            command: 'ToggleTickets',
            data: {
                ticketIds: this.selection.map((selection) => selection.id),
                remark: dialogResult.remark
            },
            showSpinner: true
        }).then(() => {
            this.emitCreated();
            this.selectedIds.clear();
        });
    }

    private async insertQuantity(): Promise<void> {
        const dialogResult = await this.dialogService.form({
            options: {
                title: this.translateService.instant('Insert Quantity'),
                titleIcons: ['faSolidCalculator'],
                message: `${this.commonService.translateService.instant(
                    'Insert the Quantity that will be distributed across the selected Tickets'
                )}.`,
                properties: [
                    {
                        name: 'quantity2',
                        label: 'Quantity 2',
                        type: 'number',
                        format: 'n2',
                        decimals: 2,
                        min: ValidationConstants.FinancialLimits.MinQuantity,
                        max: ValidationConstants.FinancialLimits.MaxQuantity,
                        required: true
                    }
                ],
                canConfirm: (model) => model.quantity2 > 0
            }
        });
        if (!dialogResult) return;

        await this.executeCommand({
            command: 'InsertTicketQuantity',
            data: { ticketIds: this.selection.map((selection) => selection.id), quantity2: dialogResult.quantity2 },
            showSpinner: true
        }).then(() => this.emitCreated());
    }

    private async debugGenerateTickets(): Promise<void> {
        const dialogResult = await this.dialogService.form({
            options: {
                title: this.translateService.instant('Generate'),
                titleIcons: ['faSolidBug'],
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to generate Tickets for this'
                )}: ${this.commonService.translateService.instant(this.naturalParentEntityName())}?`,
                properties: [
                    {
                        name: 'generationDate',
                        label: 'Generation Date',
                        type: 'datetime',
                        required: true,
                        initialValue: new Date()
                    }
                ]
            }
        });
        if (!dialogResult) return;

        await this.executeCommand({
            command: this.generateTicketsCommand(),
            data: { [this.parentEntityReference]: this.parentEntityId, generationDate: dialogResult.generationDate },
            showSpinner: true
        }).then(() => this.emitCreated());
    }

    private async userGenerateTickets(): Promise<void> {
        const dialogResult = await this.dialogService.form({
            options: {
                title: this.translateService.instant('Bill Now'),
                titleIcons: ['faSolidCashRegister'],
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to generate Tickets for this'
                )}: ${this.commonService.translateService.instant(this.naturalParentEntityName())}?`,
                properties: [
                    {
                        name: 'generationDate',
                        label: 'Generation Date',
                        type: 'datetime',
                        required: true,
                        isDisabled: () => true,
                        initialValue: new Date()
                    }
                ]
            }
        });
        if (!dialogResult) return;

        await this.executeCommand({
            command: this.generateTicketsCommand(),
            data: { [this.parentEntityReference]: this.parentEntityId, generationDate: dialogResult.generationDate },
            showSpinner: true
        }).then(() => this.emitCreated());
    }

    private async debugDeleteTickets(): Promise<void> {
        const dialogResult = await this.dialogService.confirm({
            options: {
                title: this.translateService.instant('Delete'),
                titleIcons: ['faSolidBug'],
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to delete the selected Tickets'
                )}?`
            }
        });
        if (!dialogResult) return;

        await this.executeCommand({
            command: 'DeleteTickets',
            data: { ticketIds: this.selection.map((selection) => selection.id) },
            showSpinner: true
        }).then(() => this.emitCreated());
    }

    getFilteredData() {
        let data = this.displayData;
        if (this.filter?.tarrifTypeId) {
            data = data.filter((data) => data.tariffTypeId === (this.filter.tarrifTypeId as string));
        }
        if (this.filter?.serviceId) {
            data = data.filter((data) => data.serviceId === this.filter.serviceId);
        }
        this.filteredData = data;
    }

    onQuickFilterClick(filter: string | number | string[] | number[]) {
        this.filter.tarrifTypeId = filter as string;
        this.getFilteredData();
        if (this.appGrid) {
            this.appGrid.grid.skip = 0;
            // if filter btn TERMINAL SECURITY CHARGE on ticket table is selected
            // filter id input to method 17 == TERMINAL SECURITY CHARGE
            if (filter === '17') {
                // order table data by description col
                this.appGrid.onSortChange([{ field: 'description', dir: 'asc' }]);
            } else {
                // else reset grid sort
                this.appGrid.onSortChange([]);
            }
        }
    }
}
